import { labelFrom } from './vocabularyLabels';

// What the person feels like (#443): each desire is a classifier judgment the API matches on
// "yes" or "partly". The ids are the API's; keep them in step with `DESIRE_JUDGMENTS`.
// Labels live in the `labels` catalog (`labels:desires.<id>`, #684) and are read through
// `getIntentLabel` — the filter's wording ("Laugh"), not the card signal's ("funny", #749).
export const INTENT_OPTIONS = [
  { id: 'dance', icon: 'Disc3' },
  { id: 'laugh', icon: 'Smile' },
  { id: 'think', icon: 'Lightbulb' },
  { id: 'be_moved', icon: 'Heart' },
  { id: 'unwind', icon: 'Waves' },
  { id: 'get_energized', icon: 'Zap' },
  { id: 'something_dark', icon: 'AlertCircle' },
  { id: 'meet_people', icon: 'Users' },
  { id: 'hands_on', icon: 'Hammer' },
  { id: 'play', icon: 'Target' }
];

// Literal keys, one per id, so `pnpm i18n:check` sees every one (a key built at runtime is
// invisible to it). `t` is any translator — first, like `getProviderLabel`, so
// `ids.map(getIntentLabel)` fails rather than handing it an index — and the `labels:` prefix
// names the namespace. An id the family does not hold is shown as it is.
export const getIntentLabel = labelFrom({
  dance: (t) => t('labels:desires.dance'),
  laugh: (t) => t('labels:desires.laugh'),
  think: (t) => t('labels:desires.think'),
  be_moved: (t) => t('labels:desires.be_moved'),
  unwind: (t) => t('labels:desires.unwind'),
  get_energized: (t) => t('labels:desires.get_energized'),
  something_dark: (t) => t('labels:desires.something_dark'),
  meet_people: (t) => t('labels:desires.meet_people'),
  hands_on: (t) => t('labels:desires.hands_on'),
  play: (t) => t('labels:desires.play')
}, (id) => id);

// Who it is for, one option at a time. Each option sends the API's audience values, which match
// `kids_policy`: for_children → children_primary, children_allowed → kids_welcome,
// not_for_children → adults_only, unknown and unclassified events.
export const AUDIENCE_OPTIONS = [
  { id: 'any', values: [] },
  { id: 'for_children', values: ['for_children'] },
  { id: 'not_for_children', values: ['children_allowed', 'not_for_children'] }
];

export const getAudienceLabel = labelFrom({
  any: (t) => t('labels:audience.any'),
  for_children: (t) => t('labels:audience.for_children'),
  not_for_children: (t) => t('labels:audience.not_for_children')
}, (id) => id);

const INTENT_IDS = new Set(INTENT_OPTIONS.map(option => option.id));
const normalizeValues = (values, allowedIds) => {
  const rawValues = Array.isArray(values) ? values : [values];
  const seen = new Set();
  return rawValues
    .flatMap(value => String(value || '').split(','))
    .map(value => String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_'))
    .filter(value => {
      if (!value || !allowedIds.has(value) || seen.has(value)) return false;
      seen.add(value);
      return true;
    });
};

const audienceKey = (values) => [...values].sort().join(',');

// The option whose values are exactly `values`, or null.
export const getAudienceOption = (values = []) => {
  const key = audienceKey(Array.isArray(values) ? values : [values]);
  return AUDIENCE_OPTIONS.find(option => audienceKey(option.values) === key) || null;
};

const audienceValuesOf = (raw) => [...new Set(
  (Array.isArray(raw) ? raw : [raw])
    .flatMap(value => String(value ?? '').split(','))
    .map(value => value.trim())
    .filter(Boolean)
)];

// Any as the API is sent it (#717): every audience value, which the API reads as no restriction.
// Sent rather than nothing, so a request — and the history row it leaves — says Any instead of
// looking like one from before the default existed. Filter state keeps Any as `[]`.
export const ANY_AUDIENCE_WIRE = Object.freeze(['for_children', 'children_allowed', 'not_for_children']);

const isAnyOnTheWire = (values) =>
  values.length === ANY_AUDIENCE_WIRE.length && ANY_AUDIENCE_WIRE.every(value => values.includes(value));

// Audience values are an option's set. An empty list (or Any as sent, every value) is Any; no
// value at all, or a set no option holds, is the default — so a partial or stale audience fails
// closed (#717), never open.
const normalizeAudienceValues = (values) => {
  if (values == null) return getDefaultAudience();
  const rawValues = audienceValuesOf(values);
  if (rawValues.length === 0 || isAnyOnTheWire(rawValues)) return [];
  const option = getAudienceOption(rawValues);
  return option ? [...option.values] : getDefaultAudience();
};

// The desires among filters read from outside the app — a URL, saved preferences, a history row:
// an id the family does not hold is dropped (#443). The entry points are the one place this runs;
// past them, filter state is canonical and everything else reads it as it is. Formats have no
// control any more, so nothing here reads them: a `formats` in an old link or saved filter is
// dropped rather than left narrowing a feed the reader cannot widen.
export const normalizeFilterIds = ({ intents } = {}) => ({
  intents: normalizeValues(intents || [], INTENT_IDS)
});

export const normalizeAudienceIds = (values) => normalizeAudienceValues(values);

// A copy, so filter state never holds (and never mutates) an option's own array.
export const getAudienceOptionValues = (id) => [...(AUDIENCE_OPTIONS.find(item => item.id === id)?.values || [])];

// Adults is the feed's default audience (#717): it shows no events made for children until the
// reader asks for them (Children) or for everything (Any). Search's default is Any
// (`ANY_AUDIENCE`): a name typed into search has to find the event whoever it is for. A page's
// default is what its bare URL means, so it is never written to one; anything else is, Any as
// `audience=any`. Each helper below takes the page's default, the feed's when none is given.
const DEFAULT_AUDIENCE_ID = 'not_for_children';
const ANY_AUDIENCE_PARAM = 'any';
export const ANY_AUDIENCE = Object.freeze([]);

export const getDefaultAudience = () => getAudienceOptionValues(DEFAULT_AUDIENCE_ID);

export const isDefaultAudience = (values, defaultAudience = getDefaultAudience()) =>
  audienceKey(normalizeAudienceIds(values)) === audienceKey(defaultAudience);

// Audience read from outside the app — a URL param or saved preferences: `any` is Any, an
// option's values are that option, and anything else (absent included) is the page's default.
export const restoreAudience = (raw, defaultAudience = getDefaultAudience()) => {
  const values = audienceValuesOf(raw);
  if (values.length === 1 && values[0] === ANY_AUDIENCE_PARAM) return [];
  const option = values.length > 0 ? getAudienceOption(values) : null;
  return option ? [...option.values] : [...defaultAudience];
};

// The inverse, for a URL param or saved preferences: null for the default, `any` for Any.
export const serializeAudience = (values, defaultAudience = getDefaultAudience()) => {
  const audience = normalizeAudienceIds(values);
  if (isDefaultAudience(audience, defaultAudience)) return null;
  return audience.length > 0 ? audience.join(',') : ANY_AUDIENCE_PARAM;
};

// What the audience becomes when its chip is removed: back to the default, or — removing the
// default's own chip — to Any, the one way past it.
export const audienceWithoutChip = (values, defaultAudience = getDefaultAudience()) =>
  isDefaultAudience(values, defaultAudience) ? [] : [...defaultAudience];

// The label of the option an audience filter selects; null for none (Any).
export const getAudienceSelectionLabel = (t, values) => {
  const option = getAudienceOption(values);
  return option && option.id !== 'any' ? getAudienceLabel(t, option.id) : null;
};

